import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, AlertTriangle } from 'lucide-react';
import { fmtCost, fmtInt, fmtAt } from './format';

export const RecentCallsTable = ({ range, features }) => {
  const [state, setState] = useState({ items: [], total: 0, page: 0 });
  const [feature, setFeature] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (page, reset) => {
    setLoading(true);
    try {
      const r = await api.aiCostsLog({ ...range, page, per_page: 25, ...(feature ? { feature_key: feature } : {}) });
      setState(prev => ({ total: r.data.total, page, items: reset ? r.data.items : [...prev.items, ...r.data.items] }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Aufrufe konnten nicht geladen werden'); }
    setLoading(false);
  }, [range, feature]);

  useEffect(() => { load(1, true); }, [load]);

  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="ai-costs-recent">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="font-heading text-base text-foreground">Einzelaufrufe</p>
          <p className="text-[10px] text-muted-foreground">{fmtInt(state.total)} KI-Aufrufe im Zeitraum · neueste zuerst</p>
        </div>
        <select value={feature} onChange={e => setFeature(e.target.value)} data-testid="ai-costs-recent-feature-filter"
          className="bg-secondary border border-border rounded-sm px-2 py-1 text-xs text-foreground focus:outline-none">
          <option value="">Alle Funktionen</option>
          {features.map(f => <option key={f.feature_key} value={f.feature_key}>{f.feature}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-1 pr-2 font-medium">Zeit</th>
              <th className="py-1 pr-2 font-medium">Funktion</th>
              <th className="py-1 pr-2 font-medium">Person</th>
              <th className="py-1 pr-2 font-medium hidden md:table-cell">Modell</th>
              <th className="py-1 pr-2 font-medium text-right hidden sm:table-cell">Tokens in / out</th>
              <th className="py-1 font-medium text-right">Kosten</th>
            </tr>
          </thead>
          <tbody>
            {state.items.map((r, i) => (
              <tr key={r.id || i} className="border-b border-border/50" data-testid={`ai-costs-recent-row-${i}`}>
                <td className="py-1.5 pr-2 font-mono text-muted-foreground whitespace-nowrap">{fmtAt(r.at)}</td>
                <td className="py-1.5 pr-2 text-foreground">{r.feature}{!r.ok && <AlertTriangle className="inline w-3 h-3 text-red-400 ml-1" title={r.error || 'Fehler'} />}</td>
                <td className="py-1.5 pr-2 text-foreground/80">{r.user_name || <span className="text-muted-foreground">System{r.job ? ` · ${r.job}` : ''}</span>}</td>
                <td className="py-1.5 pr-2 font-mono text-muted-foreground hidden md:table-cell">{r.model}</td>
                <td className="py-1.5 pr-2 text-right font-mono text-muted-foreground hidden sm:table-cell">{fmtInt(r.prompt_tokens)} / {fmtInt(r.completion_tokens)}</td>
                <td className="py-1.5 text-right font-mono text-amber-400">{fmtCost(r.cost_usd)}</td>
              </tr>
            ))}
            {state.items.length === 0 && !loading && (
              <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">Noch keine KI-Aufrufe im Zeitraum.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {state.items.length < state.total && (
        <button onClick={() => load(state.page + 1, false)} disabled={loading} data-testid="ai-costs-recent-load-more"
          className="mt-3 h-7 px-3 text-xs text-muted-foreground border border-border rounded-sm hover:text-foreground inline-flex items-center gap-1.5 disabled:opacity-50">
          {loading && <Loader2 className="w-3 h-3 animate-spin" />} Mehr laden
        </button>
      )}
    </div>
  );
};
